import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, ShieldCheck, Bell } from "lucide-react";
import { api, getAxiosErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ROLE_LABELS } from "@/lib/roles";

/**
 * Administration d'un compte : droits et préférences de notification.
 *
 * Deux principes gouvernent cet écran, et expliquent ce qu'on n'y trouve pas.
 *
 * Les DROITS s'affichent en trois états, pas deux : « hérité du rôle »,
 * « autorisé », « interdit ». Une case à deux positions forcerait à choisir
 * pour chaque action, et figerait le compte — une évolution de la matrice ne
 * l'atteindrait plus jamais.
 *
 * Les abonnements de VEILLE ne sont réglables que d'ici. Ils engagent
 * l'organisation : si chacun peut s'y soustraire seul, un flux censé être
 * surveillé cesse de l'être sans que personne le sache.
 */
const ONGLETS = [
  { cle: "droits", libelle: "Droits", icone: ShieldCheck },
  { cle: "notifications", libelle: "Notifications", icone: Bell },
];

const ETATS_DROIT = [
  { valeur: null, libelle: "Rôle", aide: "Suit le droit du rôle" },
  { valeur: true, libelle: "Autorisé", aide: "Accordé même si le rôle le refuse" },
  { valeur: false, libelle: "Interdit", aide: "Refusé même si le rôle l'accorde" },
];

function TroisEtats({ action, valeur, onChange }) {
  return (
    <div className="inline-flex overflow-hidden rounded-lg border border-slate-200" role="group">
      {ETATS_DROIT.map((etat) => {
        const actif = valeur === etat.valeur;
        return (
          <button
            key={String(etat.valeur)}
            type="button"
            title={etat.aide}
            onClick={() => onChange(etat.valeur)}
            aria-pressed={actif}
            className={[
              "px-2.5 py-1 text-[11px] font-semibold transition-colors",
              actif && etat.valeur === true && "bg-emerald-600 text-white",
              actif && etat.valeur === false && "bg-rose-600 text-white",
              actif && etat.valeur === null && "bg-slate-700 text-white",
              !actif && "bg-white text-slate-500 hover:bg-slate-50",
            ].filter(Boolean).join(" ")}
            data-testid={`droit-${action}-${etat.valeur === null ? "role" : etat.valeur}`}
          >
            {etat.libelle}
          </button>
        );
      })}
    </div>
  );
}

export default function UserAdminDialog({ utilisateur, onClose }) {
  const [onglet, setOnglet] = useState("droits");
  const [chargement, setChargement] = useState(true);
  const [enregistrement, setEnregistrement] = useState(false);

  const [catalogueDroits, setCatalogueDroits] = useState({ actions: [], non_surchargeables: [] });
  const [overrides, setOverrides] = useState({});

  const [catalogueEvenements, setCatalogueEvenements] = useState([]);
  const [canaux, setCanaux] = useState([]);
  const [preferences, setPreferences] = useState({});

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const [cd, d, ce, p] = await Promise.all([
        api.get("/users/permissions/catalogue"),
        api.get(`/users/${utilisateur.id}/permissions`),
        api.get("/users/notifications/catalogue"),
        api.get(`/users/${utilisateur.id}/notification-preferences`),
      ]);
      setCatalogueDroits(cd.data);
      setOverrides(d.data.overrides || {});
      setCatalogueEvenements(ce.data.evenements || []);
      setCanaux(ce.data.canaux || []);
      setPreferences(p.data.resolues || {});
    } catch (err) {
      toast.error(getAxiosErrorMessage(err));
    } finally {
      setChargement(false);
    }
  }, [utilisateur.id]);

  useEffect(() => { charger(); }, [charger]);

  const parCategorie = useMemo(() => {
    const groupes = new Map();
    for (const evenement of catalogueEvenements) {
      if (!groupes.has(evenement.categorie)) groupes.set(evenement.categorie, []);
      groupes.get(evenement.categorie).push(evenement);
    }
    return [...groupes.entries()];
  }, [catalogueEvenements]);

  const poserDroit = async (action, valeur) => {
    const precedent = overrides;
    // Optimiste : l'écran compte une trentaine d'actions, attendre le serveur à
    // chaque clic rendrait le réglage pénible.
    setOverrides((o) => {
      const suivant = { ...o };
      if (valeur === null) delete suivant[action];
      else suivant[action] = valeur;
      return suivant;
    });
    try {
      await api.put(`/users/${utilisateur.id}/permissions`, {
        overrides: { [action]: valeur },
      });
    } catch (err) {
      setOverrides(precedent);
      toast.error(getAxiosErrorMessage(err));
    }
  };

  const posetPreference = async (canal, cle, valeur) => {
    const precedent = preferences;
    setPreferences((p) => ({ ...p, [canal]: { ...(p[canal] || {}), [cle]: valeur } }));
    setEnregistrement(true);
    try {
      await api.put(`/users/${utilisateur.id}/notification-preferences`, {
        preferences: { [canal]: { [cle]: valeur } },
      });
    } catch (err) {
      setPreferences(precedent);
      toast.error(getAxiosErrorMessage(err));
    } finally {
      setEnregistrement(false);
    }
  };

  return (
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="user-admin-dialog">
      <DialogHeader>
        <DialogTitle className="font-heading">{utilisateur.name}</DialogTitle>
        <DialogDescription>
          {utilisateur.email} · {ROLE_LABELS[utilisateur.role] || utilisateur.role}
        </DialogDescription>
      </DialogHeader>

      <div className="flex gap-1 border-b border-slate-100">
        {ONGLETS.map(({ cle, libelle, icone: Icone }) => (
          <button
            key={cle}
            type="button"
            onClick={() => setOnglet(cle)}
            className={[
              "inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-semibold transition-colors",
              onglet === cle
                ? "border-blue-600 text-blue-700"
                : "border-transparent text-slate-500 hover:text-slate-800",
            ].join(" ")}
            data-testid={`onglet-${cle}`}
          >
            <Icone size={14} strokeWidth={1.75} /> {libelle}
          </button>
        ))}
      </div>

      {chargement ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
        </div>
      ) : onglet === "droits" ? (
        <div data-testid="panneau-droits">
          <p className="mb-4 text-sm text-slate-500">
            Le rôle <strong>{ROLE_LABELS[utilisateur.role] || utilisateur.role}</strong> reste la
            base. Une exception ne s'applique qu'à ce compte ; « Rôle » la retire.
          </p>

          {utilisateur.role === "admin" ? (
            <p
              className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
              data-testid="admin-intouchable"
            >
              Ce compte est administrateur : aucune exception ne s'applique à lui.
              Le priver d'un droit rendrait toute reprise impossible depuis l'interface.
            </p>
          ) : null}

          <ul className="divide-y divide-slate-50">
            {catalogueDroits.actions.map((action) => (
              <li key={action} className="flex items-center justify-between gap-4 py-2">
                <code className="min-w-0 truncate text-xs text-slate-700">{action}</code>
                <TroisEtats
                  action={action}
                  valeur={action in overrides ? overrides[action] : null}
                  onChange={(v) => poserDroit(action, v)}
                />
              </li>
            ))}
          </ul>

          {catalogueDroits.non_surchargeables.length > 0 ? (
            <p className="mt-4 text-xs text-slate-400" data-testid="droits-figes">
              Non modifiables ici : {catalogueDroits.non_surchargeables.join(", ")}.
              Ces droits ne se règlent qu'au niveau de la matrice.
            </p>
          ) : null}
        </div>
      ) : (
        <div data-testid="panneau-notifications">
          <p className="mb-4 text-sm text-slate-500">
            Les abonnements de veille se règlent ici, et nulle part ailleurs : ils engagent
            l'organisation. Chacun reste libre de choisir son canal pour ce qui le concerne.
          </p>

          {parCategorie.map(([categorie, evenements]) => (
            <section key={categorie} className="mb-5">
              <h4 className="mb-2 text-xs font-bold uppercase tracking-[0.15em] text-slate-400">
                {categorie}
              </h4>
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className="pb-1 text-left font-medium text-slate-400" />
                    {canaux.map((canal) => (
                      <th
                        key={canal}
                        className="w-24 pb-1 text-center text-[11px] font-semibold uppercase tracking-wider text-slate-400"
                      >
                        {canal === "email" ? "Email" : "Application"}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {evenements.map((evenement) => (
                    <tr key={evenement.cle}>
                      <td className="py-2 pr-3 text-slate-700">{evenement.libelle}</td>
                      {canaux.map((canal) => (
                        <td key={canal} className="py-2 text-center">
                          <Switch
                            checked={!!preferences[canal]?.[evenement.cle]}
                            disabled={enregistrement}
                            onCheckedChange={(v) => posetPreference(canal, evenement.cle, v)}
                            data-testid={`pref-${canal}-${evenement.cle}`}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))}
        </div>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">
          Fermer
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
